import React from 'react';
import { View, Text, Animated, ScrollView, Platform, StyleSheet } from 'react-native';
import ptExecutionSectionStyles from '../styles/PTExecutionSectionStyles';
import PTExecutionTrendChart from './PTExecutionTrendChart';
import { BreakdownData, TrendData } from '../pages/PerfectTruckAnalytics/perfectTruckKpiApi';
import ChartSkeleton from './ChartSkeleton';
import { useTheme } from '../../../theme/useTheme';

interface PTExecutionSectionProps {
  trend?: TrendData | null;
  breakdown?: BreakdownData | null;
  loading?: boolean;
}

/** Dark-mode overrides for PTExecutionSection — applied inline on top of the static light styles. */
const darkStyles = StyleSheet.create({
  card: {
    backgroundColor: '#202020',
    borderColor: '#404040',
    borderWidth: 1,
    shadowOpacity: 0,
    elevation: 0,
  },
  title: {
    color: '#F2F2F2',
  },
  subTitle: {
    color: '#8A9BB0',
  },
  emptyText: {
    color: '#B0B0B0',
  },
  regionLabel: {
    color: '#B0B0B0',
  },
  progressTrack: {
    backgroundColor: '#1A2B3D',
  },
  trendLegendText: {
    color: '#B0B0B0',
  },
});

const PTExecutionSection: React.FC<PTExecutionSectionProps> = ({ trend, breakdown, loading = false }) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const styles = ptExecutionSectionStyles;

  // Filter out data points with invalid labels (e.g. '?') from the API
  const rawTrendPoints = (trend?.dataPoints ?? [])
    .filter((dp) => dp.label != null && dp.label !== '?')
    .map((dp) => {
      const explicitSubLabel = dp.subLabel;
      let derivedSubLabel: string | undefined;
      if (!explicitSubLabel && trend?.level === 'week' && dp.period != null) {
        derivedSubLabel = `P${dp.period}`;
      } else if (!explicitSubLabel && trend?.level === 'period' && dp.year != null) {
        derivedSubLabel = `'${String(dp.year).slice(-2)}`;
      }
      return {
        label: dp.label,
        year: dp.year,
        percentage: dp.percentage,
        highlighted: dp.isHighlighted ?? dp.highlighted ?? false,
        subLabel: explicitSubLabel ?? derivedSubLabel,
      };
    });

  // For year-level trend: fill every year between min and current year so
  // the X-axis has no gaps (e.g. 2024→2026 shows 2024, 2025, 2026).
  const trendPoints = (() => {
    if (trend?.level !== 'year' || rawTrendPoints.length === 0) return rawTrendPoints;

    const currentYear = new Date().getFullYear();
    const yearMap = new Map(rawTrendPoints.map((p) => [Number(p.year ?? p.label), p]));
    // Range: minYear up to the greater of max data year or current year
    const maxYear = Math.max(currentYear, ...Array.from(yearMap.keys()));
    // Always show at least 2 years so the chart renders as a line, not a lone dot
    const minYear = Math.min(...Array.from(yearMap.keys()), maxYear - 1);

    const filled = [];
    for (let y = minYear; y <= maxYear; y++) {
      if (yearMap.has(y)) {
        filled.push(yearMap.get(y)!);
      } else {
        filled.push({ label: String(y), year: y, percentage: 0, highlighted: false, subLabel: undefined });
      }
    }
    return filled;
  })();

  const chartSubtitle = trend?.chartSubtitle ?? 'Trend by Year';

  // Sort bars descending by percentage
  const barData = [...(breakdown?.bars ?? [])].sort((a, b) => b.percentage - a.percentage);
  const breakdownTitle = breakdown?.title ?? 'by Region / Unit';

  // Compute a fixed label column width based on the longest label text so all
  // bars start at the same X position (approx 7px per char at fontSize 12 medium).
  const labelWidth = barData.length > 0
    ? Math.max(...barData.map((item) => item.label.length)) * 7.5 + 8
    : 100;

  return (
    <View style={styles.sectionRow}>
      <View style={[styles.card, isDark && darkStyles.card]}>
        <View style={styles.headerRow}>
          <Text style={[styles.title, isDark && darkStyles.title]}>Perfect Truck Execution</Text>
          <Text style={[styles.subTitle, isDark && darkStyles.subTitle]}>{chartSubtitle}</Text>
        </View>

        {loading ? (
          <ChartSkeleton height={220} />
        ) : trendPoints.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={[styles.emptyText, isDark && darkStyles.emptyText]}>No data available</Text>
          </View>
        ) : (
          <PTExecutionTrendChart data={trendPoints} legendLabel="Perfect Truck Execution" />
        )}
      </View>

      <View style={[styles.card, isDark && darkStyles.card]}>
        <View style={styles.headerRow}>
          <Text style={[styles.title, isDark && darkStyles.title]}>Perfect Truck Execution</Text>
          <Text style={[styles.subTitle, isDark && darkStyles.subTitle]}>{breakdownTitle}</Text>
        </View>

        <View style={styles.regionListWrap}>
          {loading ? (
            <ChartSkeleton height={220} barMode />
          ) : barData.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Text style={[styles.emptyText, isDark && darkStyles.emptyText]}>No data available</Text>
            </View>
          ) : (
            // Keep a fixed height matching the trend chart so both cards align visually
            // and the bars list scrolls vertically when long (restores previous behavior).
            <ScrollView style={{ height: Platform.OS === 'ios' ? 178 : 220 }} contentContainerStyle={{ paddingVertical: 8 }}>
              {barData.map((item) => {
                // Fallback: if API returns 0 but there are approved routes, compute client-side
                const pct =
                  item.percentage > 0
                    ? item.percentage
                    : item.targetRoutes > 0
                    ? (item.approvedRoutes / item.targetRoutes) * 100
                    : 0;
                const barWidth = Math.min(100, Math.max(0, pct));
                const displayPct = pct >= 1 ? `${Math.round(pct)}%` : pct > 0 ? `<1%` : `0%`;
                // Below this fill width the bar isn't wide enough to legibly contain
                // the white label — render the label just outside the fill instead so
                // low percentages (e.g. 4%–7%) stay visually distinct from one another
                // rather than all rendering as the same minimum-width bar.
                const showLabelOutside = barWidth < 15;
                const isBelowTarget = item.isBelowTarget ?? item.belowTarget ?? false;
                const outsideLabelColorStyle = isBelowTarget
                  ? styles.progressOutsideLabelRed
                  : styles.progressOutsideLabelBlue;
                // 0% and <1%/1% renders as a tiny sliver — nudge the label further
                // right so it doesn't sit hidden behind/under the colored bar.
                const isTinyValue = pct <= 1;
                return (
                  <View key={item.label} style={styles.regionRow}>
                    <Text style={[styles.regionLabel, isDark && darkStyles.regionLabel, { width: labelWidth }]} numberOfLines={1}>
                      {item.label}
                    </Text>
                    <View style={styles.progressTrackWrap}>
                      <View style={[styles.progressTrack, isDark && darkStyles.progressTrack]}>
                        <View
                          style={[
                            styles.progressFill,
                            isBelowTarget ? styles.progressFillRed : styles.progressFillBlue,
                            { width: `${barWidth}%` },
                          ]}
                        >
                          {!showLabelOutside && <Text style={styles.progressText}>{displayPct}</Text>}
                        </View>
                      </View>
                      {showLabelOutside && (
                        <Text
                          style={[
                            styles.progressOutsideLabel,
                            outsideLabelColorStyle,
                            isTinyValue && styles.progressOutsideLabelTight,
                            { left: `${barWidth}%` },
                          ]}
                        >
                          {displayPct}
                        </Text>
                      )}
                    </View>
                  </View>
                );
              })}
            </ScrollView>
          )}
        </View>
      </View>
    </View>
  );
};

export default PTExecutionSection;

